/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The value slot, read** (`N11f` · `#1177` · `P1`).
 *
 * A mapping row answers three separate questions — *what literal did you author*, *what
 * macro does this resolve from*, and *what should we send if it resolves to nothing* — and
 * until 2026-08-26 the stored document had **one slot** for all three (`defaultValue` on a
 * parameter, `value` on a header). So the macro and its default were the same string, and a
 * macro mapping could not carry a default on any surface: not a missing control, an
 * unrepresentable state.
 *
 * The slots are split. This module is the **one reader** of a document written before the
 * split — it puts each legacy value in the slot it was already behaving as, so the wire does
 * not move. It lives in `libs/dto` rather than in the driver because a parameter row and a
 * header row are the same question with different key names, and answering it twice is how
 * the two surfaces came to disagree about what a mapping is (`#1128`).
 *
 * **Not a compatibility shim with an expiry** — it is how a stored document is read. The
 * console writes the split shape on the next save; nothing rewrites a document that is not
 * being edited, and nothing has to.
 */

/** A row's three value answers, each in its own slot. */
export interface MappingValueSlots {
  /** The authored literal. Absent when the row resolves from somewhere. */
  literal?: string | number | boolean;
  /** The macro token as authored, delimiters included — `{ip}`, `{{IP}}`, `[timestamp]`. */
  macro?: string;
  /** What to send when the derived value resolves to nothing. */
  default?: string;
}

/** A string that is exactly one macro token, in any of the three legal spellings. */
const SINGLE_MACRO_TOKEN = /^[[{]\{?\s*[A-Za-z0-9_]+\s*\}?[\]}]$/;

/** Is this authored string a bare macro token (and therefore not a literal)? */
export function isMacroToken(value: unknown): value is string {
  return typeof value === 'string' && SINGLE_MACRO_TOKEN.test(value.trim());
}

/** The macro name inside a token, without delimiters — `{{IP}}` → `IP`. */
export function macroTokenName(token: string): string {
  return token
    .trim()
    .replace(/^[[{]\{?\s*/, '')
    .replace(/\s*\}?[\]}]$/, '');
}

/**
 * Read a stored row's value slots, splitting a pre-`N11f` document.
 *
 * The three legacy readings, and each is what the driver was **already doing** with that
 * value — established by running the drivers' specs against the split, not by reading the
 * field names:
 *
 * | The one slot held | It was behaving as | Because |
 * | --- | --- | --- |
 * | a bare `{token}` | the **macro** | the driver returned it and the surface's expander resolved it |
 * | anything, on a `type: 'static'` row | the **literal** | the driver returned it *before* trying to resolve anything, and *a static parameter is the author's* |
 * | anything else | the **default** | the driver consulted it only after context resolution found nothing — which is what `maxPodDuration: { defaultValue: 180 }` on the seeded sources has always meant |
 *
 * **The third row is the one that surprises**, and getting it wrong is why `type` cannot
 * simply be deleted: an untyped `defaultValue` is a *default*, exactly as its name says, and
 * reading it as an authored literal would have made a seeded default outrank the session
 * value it exists to stand in for.
 *
 * @param stored the row's raw slots. `value` is the parameter row's second spelling of
 *   `defaultValue`, which the driver prefers (`#1037` retires one of them).
 */
export function readMappingValueSlots(stored: {
  value?: string | number | boolean;
  defaultValue?: string | number | boolean;
  macro?: string;
  default?: string;
  /** Pre-`#1216` spelling of {@link MappingValueSlots.default}. */
  fallback?: string;
  mappingSource?: string;
  expression?: unknown;
  /** The pre-split marker. Only `'static'` carries meaning here: *this is a literal*. */
  type?: string;
}): MappingValueSlots {
  const authored = stored.value !== undefined ? stored.value : stored.defaultValue;
  const isLiteral = stored.type === 'static';

  // A document written since the split says which slot it means, so nothing is inferred.
  // `macro` and `default` are read independently: a row may legitimately carry only one.
  // `fallback` is the pre-`#1216` spelling of `default` (`X3`) and is read here for the same
  // reason the legacy readings below exist — a stored document is not rewritten until it is
  // next saved, and the two backups in `examples/` both carry it.
  const storedDefault = stored.default !== undefined ? stored.default : stored.fallback;
  const split: MappingValueSlots = {};
  if (typeof stored.macro === 'string' && stored.macro !== '') split.macro = stored.macro;
  if (typeof storedDefault === 'string' && storedDefault !== '') split.default = storedDefault;

  if (split.macro !== undefined || split.default !== undefined) {
    // The remaining slot is the literal, and only when the document has not already said the
    // same value is one of the other two — a split document still carrying the legacy token
    // in `defaultValue` (an older console, a hand-edited row) must not send it as a literal.
    if (authored !== undefined && !isMacroToken(authored) && isLiteral) {
      split.literal = authored;
    }
    return split;
  }

  if (authored === undefined) return split;
  if (isMacroToken(authored)) return { macro: String(authored) };
  if (isLiteral) return { literal: authored };
  return { default: String(authored) };
}

/**
 * **A stored mapping's derived value, as the one expression it is** (`X58`, `#1428`).
 *
 * A mapping stores a static or an expression. Before `X58` a derived value landed in one of three
 * slots: `macro`, `mappingSource` (a typed path) or `expression` (a path with a chip, or anything
 * richer), so every reader had to know all three and two did not (`2e`'s `[*]`, `#1427`). This is the
 * **one legacy read** (the `X3` pattern): each store calls it where it reads a document, and every
 * reader after that sees only `expression`.
 *
 * | Stored | Reads as |
 * | --- | --- |
 * | `expression` | itself |
 * | `macro` (with or without a `mappingSource` beside it) | `{ type: 'macro', name }` |
 * | `mappingSource` | `{ type: 'calculated', path }` |
 *
 * **A macro beside a path reads as the macro.** That is the shape the console wrote for a macro row
 * before `N11f` (`session.user.userAgent` beside `{ua}`), and the macro was the path's backstop. The
 * macro's own resolution already includes that path (its catalog entry declares it, or chains through
 * it), so the macro alone sends what the pair did.
 *
 * The row's `default` rides on the node, where the expression editor reads it. Typed structurally
 * rather than as `MacroExpression`, so this file still imports nothing (the console aliases it).
 */
export function foldMappingExpression(stored: {
  macro?: string;
  mappingSource?: string;
  expression?: unknown;
  default?: string;
}): unknown {
  if (stored.expression && typeof stored.expression === 'object') return stored.expression;
  const withDefault = stored.default !== undefined && stored.default !== '' ? { default: stored.default } : {};
  if (typeof stored.macro === 'string' && stored.macro !== '') {
    return { type: 'macro', name: macroTokenName(stored.macro), ...withDefault };
  }
  if (typeof stored.mappingSource === 'string' && stored.mappingSource !== '') {
    return { type: 'calculated', path: stored.mappingSource, ...withDefault };
  }
  return undefined;
}

/**
 * **The macro or path a folded expression names, where it is one of the two** — what a reader that
 * compares concepts (the auto-populated dedupe, `displacedAutoParam`) reads from it. A richer
 * expression names neither, and answers nothing.
 */
export function expressionSourceOf(expression: unknown): { macro?: string; mappingSource?: string } {
  if (!expression || typeof expression !== 'object') return {};
  const node = expression as { type?: unknown; name?: unknown; path?: unknown };
  if (node.type === 'macro' && typeof node.name === 'string') return { macro: `{${node.name}}` };
  if (node.type === 'calculated' && typeof node.path === 'string') return { mappingSource: node.path };
  return {};
}

/**
 * **The keys that make a stored header value a structured mapping rather than a literal** — declared
 * once here, read by the driver and the console (slice `4b-ii`).
 *
 * It was two hand-kept copies, one in `demand-source-driver.ts` and one in the console's header
 * adapter, each with a comment asking the other to keep up: a key missing from the driver's copy makes
 * the whole object read as a plain string and ship `[object Object]`. Adding `importance` (`#1354`) to
 * one of two copies is exactly that defect, so the list moved before the key was added.
 *
 * `importance` is here so a row whose only authored fact is *required* is still read as structured —
 * it changes nothing sent (`X46`), and the driver never reads it.
 */
export const HEADER_VALUE_SLOTS = [
  'value',
  'macro',
  'default',
  'fallback',
  'mappingSource',
  'expression',
  'suppress',
  'importance',
  // `CR2` (`#1371`): a row declared secret is structured even when its literal is all it says.
  'secret',
] as const;

/** Is this stored header value a structured mapping, as opposed to a literal string? */
export function isStructuredHeaderValue(
  value: unknown,
): value is Partial<Record<(typeof HEADER_VALUE_SLOTS)[number], unknown>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return HEADER_VALUE_SLOTS.some((slot) => slot in (value as Record<string, unknown>));
}
